/**
 * VoyageLiveFeed - Live log feed panel for Voyage Management
 * Shows contextual log entries related to the current voyage
 */

import React, { useMemo, useState } from 'react';
import { 
  Clock, Navigation, Cloud, AlertTriangle, Users, 
  FileText, ChevronRight, ExternalLink, RefreshCw,
  MapPin, Anchor, Filter
} from 'lucide-react';
import { Badge } from '../../../catalyst/badge';

const VoyageLiveFeed = ({ 
  logEntries = [], 
  activeVoyage,
  onOpenFullLog,
  maxEntries = 20 
}) => {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Filter entries for current voyage and category
  const filteredEntries = useMemo(() => {
    let filtered = [...logEntries];

    // Show only current voyage entries if voyage is active
    if (activeVoyage) {
      filtered = filtered.filter(entry => 
        entry.voyageId === activeVoyage.id || 
        entry.type === 'VOYAGE_START' || 
        entry.type === 'VOYAGE_END'
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(entry => entry.category === categoryFilter);
    }

    // Sort by newest first and limit
    return filtered
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, maxEntries);
  }, [logEntries, activeVoyage, categoryFilter, maxEntries]);

  // Get time ago string
  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hr ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Get entry icon
  const getEntryIcon = (entry) => {
    const icons = {
      'POSITION': MapPin,
      'WEATHER': Cloud,
      'NAVIGATION': Navigation,
      'CREW': Users,
      'SAFETY': AlertTriangle,
      'LET_GO': Anchor,
      'ALL_FAST': Anchor
    };
    const Icon = icons[entry.type] || FileText;
    return <Icon className="h-3 w-3" />;
  };

  // Get entry color
  const getEntryColor = (entry) => {
    if (entry.type === 'LET_GO' || entry.type === 'VOYAGE_START') return 'green';
    if (entry.type === 'ALL_FAST' || entry.type === 'VOYAGE_END') return 'orange';
    if (entry.category === 'safety') return 'red';
    if (entry.category === 'navigation') return 'blue';
    return 'gray';
  };

  return (
    <div className="bg-white rounded-lg shadow h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-900">
            Live Feed
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`p-1 rounded transition-colors ${
                autoRefresh ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600'
              }`}
              title={autoRefresh ? 'Auto-refresh on' : 'Auto-refresh off'}
            >
              <RefreshCw className={`h-3 w-3 ${autoRefresh ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onOpenFullLog}
              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
              title="Open full log"
            >
              <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        </div>
        
        {/* Category filter */}
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
              categoryFilter === 'all' 
                ? 'bg-gray-800 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setCategoryFilter('navigation')}
            className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
              categoryFilter === 'navigation' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Nav
          </button>
          <button
            onClick={() => setCategoryFilter('weather')}
            className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
              categoryFilter === 'weather' 
                ? 'bg-gray-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Weather
          </button>
          <button
            onClick={() => setCategoryFilter('safety')}
            className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
              categoryFilter === 'safety' 
                ? 'bg-orange-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Safety
          </button>
        </div>
      </div>

      {/* Voyage status bar */}
      {activeVoyage && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-blue-900">
                {activeVoyage.departurePort} → {activeVoyage.destinationPort}
              </span>
            </div>
            <Badge color="blue" size="sm">
              {activeVoyage.status || 'Active'}
            </Badge>
          </div>
        </div>
      )}

      {/* Feed entries */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {filteredEntries.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="h-8 w-8 mx-auto text-gray-300 mb-2" />
            <p className="text-xs text-gray-500">No entries yet</p>
            <p className="text-xs text-gray-400 mt-1">
              {activeVoyage ? 'Voyage activities will appear here' : 'Start a voyage to see entries'}
            </p>
          </div>
        ) : (
          filteredEntries.map((entry) => (
            <div 
              key={entry.id} 
              className="group hover:bg-gray-50 rounded p-2 transition-colors cursor-pointer"
            >
              <div className="flex items-start gap-2">
                {/* Time column */}
                <div className="flex-shrink-0 w-12 text-right">
                  <p className="text-xs text-gray-500">
                    {new Date(entry.timestamp).toLocaleTimeString('en-GB', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
                
                {/* Icon */}
                <div className={`flex-shrink-0 p-1 rounded ${
                  getEntryColor(entry) === 'green' ? 'bg-green-100 text-green-600' :
                  getEntryColor(entry) === 'orange' ? 'bg-orange-100 text-orange-600' :
                  getEntryColor(entry) === 'red' ? 'bg-red-100 text-red-600' :
                  getEntryColor(entry) === 'blue' ? 'bg-blue-100 text-blue-600' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {getEntryIcon(entry)}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {entry.summary}
                  </p>
                  {entry.description && (
                    <p className="text-xs text-gray-500 truncate">
                      {entry.description}
                    </p>
                  )}
                  {entry.position && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {entry.position.lat}, {entry.position.lon}
                    </p>
                  )}
                </div>
                
                {/* Time ago */}
                <div className="flex-shrink-0 text-xs text-gray-400 group-hover:text-gray-600">
                  {getTimeAgo(entry.timestamp)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer with entry count */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">
            {filteredEntries.length} of {logEntries.length} entries
          </span>
          <button
            onClick={onOpenFullLog}
            className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            Open Full Log
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoyageLiveFeed;